import * as types from '../actions/types';

const initialState = {
  // Плоский массив всех объектов схемы (загружается с сервера)
  objects: [],

  // Выбранные элементы
  selectedObjectId: null,
  selectedAreaId: null,

  // Счётчики для принудительного обновления
  areasRefreshCount: 0,
  imageRefreshCount: 0,

  // Режим редактора: 'select' | 'draw' | 'edit'
  editorMode: 'select',

  // Точки текущего рисуемого полигона
  currentDrawingPoints: [],

  // Zoom/Pan состояние
  stageScale: 1,
  stagePosition: { x: 0, y: 0 },

  // Флаг несохраненных изменений
  hasChanges: false,

  // ID объектов с несохранёнными изменениями
  modifiedObjectIds: new Set(),

  // Несохранённые изменения областей: { areaId: { target_object_id, ... } }
  pendingAreaUpdates: {},

  // Справочники УМБ
  availableBuildings: [],
  availableBuildingsLoading: false,
  availableRooms: [],
  availableRoomsLoading: false,

  // Состояния загрузки
  treeLoading: false,
  saving: false
};

export default function infoTerminal(state = initialState, action) {
  switch (action.type) {
    case types.SET_INFO_TERMINAL_OBJECTS:
      return { ...state, objects: action.payload };

    case types.SET_INFO_TERMINAL_SELECTED_OBJECT:
      return {
        ...state,
        selectedObjectId: action.payload,
        selectedAreaId: null,
        editorMode: 'select',
        currentDrawingPoints: []
      };

    case types.SET_INFO_TERMINAL_SELECTED_AREA:
      return { ...state, selectedAreaId: action.payload };

    case types.SET_INFO_TERMINAL_EDITOR_MODE:
      return {
        ...state,
        editorMode: action.payload,
        currentDrawingPoints: action.payload === 'draw' ? [] : state.currentDrawingPoints
      };

    case types.SET_INFO_TERMINAL_DRAWING_POINTS:
      return { ...state, currentDrawingPoints: action.payload };

    case types.ADD_INFO_TERMINAL_DRAWING_POINT:
      return {
        ...state,
        currentDrawingPoints: [...state.currentDrawingPoints, action.payload]
      };

    case types.CLEAR_INFO_TERMINAL_DRAWING_POINTS:
      return { ...state, currentDrawingPoints: [] };

    case types.SET_INFO_TERMINAL_STAGE_SCALE:
      return { ...state, stageScale: action.payload };

    case types.SET_INFO_TERMINAL_STAGE_POSITION:
      return { ...state, stagePosition: action.payload };

    case types.SET_INFO_TERMINAL_HAS_CHANGES:
      return {
        ...state,
        hasChanges: action.payload,
        // Очищаем списки изменённых объектов и областей при сбросе флага
        modifiedObjectIds: action.payload ? state.modifiedObjectIds : new Set(),
        pendingAreaUpdates: action.payload ? state.pendingAreaUpdates : {}
      };

    case types.ADD_INFO_TERMINAL_OBJECT:
      return {
        ...state,
        objects: [...state.objects, action.payload],
        hasChanges: true
      };

    case types.UPDATE_INFO_TERMINAL_OBJECT: {
      const newModifiedIds = new Set(state.modifiedObjectIds);
      newModifiedIds.add(action.payload.id);
      return {
        ...state,
        objects: state.objects.map(obj =>
          obj.id === action.payload.id ? { ...obj, ...action.payload } : obj
        ),
        hasChanges: true,
        modifiedObjectIds: newModifiedIds
      };
    }

    case types.DELETE_INFO_TERMINAL_OBJECT: {
      // Удаляем объект и всех его потомков
      const idsToDelete = new Set();
      const collectChildIds = (parentId) => {
        idsToDelete.add(parentId);
        state.objects.forEach(obj => {
          if (obj.parentId === parentId) {
            collectChildIds(obj.id);
          }
        });
      };
      collectChildIds(action.payload);

      // Убираем удалённые объекты из списка несохранённых изменений
      const newModifiedIds = new Set(state.modifiedObjectIds);
      idsToDelete.forEach(id => newModifiedIds.delete(id));

      // Удаление уже выполнено на сервере — пересчитываем флаг изменений
      const hasRemainingChanges = newModifiedIds.size > 0 || Object.keys(state.pendingAreaUpdates).length > 0;

      return {
        ...state,
        objects: state.objects.filter(obj => !idsToDelete.has(obj.id)),
        selectedObjectId: idsToDelete.has(state.selectedObjectId) ? null : state.selectedObjectId,
        selectedAreaId: null,
        modifiedObjectIds: newModifiedIds,
        hasChanges: hasRemainingChanges
      };
    }

    case types.ADD_INFO_TERMINAL_AREA: {
      const { objectId, area } = action.payload;
      const newModifiedIds = new Set(state.modifiedObjectIds);
      newModifiedIds.add(objectId);
      return {
        ...state,
        objects: state.objects.map(obj => {
          if (obj.id === objectId) {
            return {
              ...obj,
              areas: [...(obj.areas || []), area]
            };
          }
          return obj;
        }),
        hasChanges: true,
        modifiedObjectIds: newModifiedIds
      };
    }

    case types.UPDATE_INFO_TERMINAL_AREA: {
      const { areaId, updates } = action.payload;
      return {
        ...state,
        pendingAreaUpdates: {
          ...state.pendingAreaUpdates,
          [areaId]: {
            ...(state.pendingAreaUpdates[areaId] || {}),
            ...updates
          }
        },
        hasChanges: true
      };
    }

    case types.DELETE_INFO_TERMINAL_AREA: {
      const { objectId, areaId } = action.payload;
      const newModifiedIds = new Set(state.modifiedObjectIds);
      newModifiedIds.add(objectId);
      return {
        ...state,
        objects: state.objects.map(obj => {
          if (obj.id === objectId) {
            return {
              ...obj,
              areas: (obj.areas || []).filter(area => area.id !== areaId)
            };
          }
          return obj;
        }),
        selectedAreaId: state.selectedAreaId === areaId ? null : state.selectedAreaId,
        hasChanges: true,
        modifiedObjectIds: newModifiedIds
      };
    }

    // Справочники УМБ
    case types.SET_INFO_TERMINAL_AVAILABLE_BUILDINGS:
      return { ...state, availableBuildings: action.payload };

    case types.SET_INFO_TERMINAL_AVAILABLE_BUILDINGS_LOADING:
      return { ...state, availableBuildingsLoading: action.payload };

    case types.SET_INFO_TERMINAL_AVAILABLE_ROOMS:
      return { ...state, availableRooms: action.payload };

    case types.SET_INFO_TERMINAL_AVAILABLE_ROOMS_LOADING:
      return { ...state, availableRoomsLoading: action.payload };

    case types.SET_INFO_TERMINAL_TREE_LOADING:
      return { ...state, treeLoading: action.payload };

    case types.SET_INFO_TERMINAL_SAVING:
      return { ...state, saving: action.payload };

    case types.REFRESH_INFO_TERMINAL_AREAS:
      return { ...state, areasRefreshCount: state.areasRefreshCount + 1 };

    case types.REFRESH_INFO_TERMINAL_IMAGE:
      return { ...state, imageRefreshCount: state.imageRefreshCount + 1 };

    default:
      return state;
  }
}
